import React, {
    FC,
    useMemo,
    useRef,
    useCallback,
    useState,
    MouseEvent
} from "react"
import {
    ClickAwayListener,
    Popper,
    MultipleChoiceDropdown,
    OutlinedButton,
    Panel
} from "dash-styled"
import { useTagList } from "../../../../../../../Hooks/useTagList"
import { ReactComponent as TagIcon } from "icons/lib/tag.svg"
import { ReactComponent as CloseIcon } from "icons/lib/close-icon.svg"
import { useBoolean } from "../../../../../../../Hooks/useBoolean"
import {
    getValue,
    hasValue
} from "../../../../../../../Models/Client/FetchState"

interface TagsViewEditProps {
    appID: string
    tags: string[]
    onAdd?: (value: string, isNew: boolean) => void
    onRemove?: (value: string) => void
}

export const TagsViewEdit: FC<TagsViewEditProps> = ({
    appID,
    tags,
    onAdd,
    onRemove
}) => {
    const [isOpen, , close, toggle] = useBoolean(false)
    const anchorEl = useRef<HTMLButtonElement>(null)

    const handleClickAway = useCallback(
        (e: MouseEvent<Document>) => {
            if (
                e.target instanceof Node &&
                anchorEl.current !== null &&
                !anchorEl.current.contains(e.target)
            ) {
                close()
            }
        },
        [close]
    )

    const [tagSearchKeyword, setTagSearchKeyword] = useState("")
    const tagsFetch = useTagList(appID, tagSearchKeyword)
    const [selectedTags, setSelectedTags] = useState<string[]>(tags)

    const notUsedTags = useMemo(() => {
        const registeredTags = getValue(tagsFetch) ?? []
        return registeredTags.filter(regTag => !selectedTags.includes(regTag))
    }, [tagsFetch, selectedTags])

    const handleAddTag = useMemo(
        () => (tag: string) => {
            if (!selectedTags.includes(tag)) {
                setSelectedTags(selectedTags => [...selectedTags, tag])
                return onAdd?.(tag, true)
            }
        },
        [onAdd, selectedTags]
    )

    const handleToggleTag = useCallback(
        (affectedTag: string) => {
            setSelectedTags(selectedTags =>
                selectedTags.includes(affectedTag)
                    ? selectedTags.filter(tag => tag !== affectedTag)
                    : [...selectedTags, affectedTag]
            )

            if (tags.includes(affectedTag)) {
                onRemove?.(affectedTag)
            } else {
                onAdd?.(affectedTag, false)
            }
        },
        [onAdd, onRemove, tags]
    )

    return (
        <>
            <OutlinedButton
                ref={anchorEl}
                Icon={TagIcon}
                marginRight="2"
                label="Tags"
                onClick={toggle}
            />
            <ClickAwayListener onClickAway={handleClickAway}>
                <Popper
                    open={isOpen}
                    anchorEl={anchorEl.current}
                    transition={true}
                    className="pt-2 pb-1 cursor-auto"
                    disablePortal={true}
                    placement="bottom-start"
                >
                    <Panel
                        className="p-1 min-w-48 rounded-lg border border-rhino-300"
                        noPadding={true}
                        noRadius={true}
                    >
                        {selectedTags.map(tag => (
                            <div
                                key={tag}
                                className="group flex items-center px-4 py-2 hover:bg-smoke-200"
                            >
                                <div className="flex-grow leading-none">
                                    {tag}
                                </div>
                                <div
                                    className="p-1 ml-4 cursor-pointer"
                                    onClick={() => handleToggleTag(tag)}
                                >
                                    <CloseIcon className="text-rhino-900 invisible group-hover:visible" />
                                </div>
                            </div>
                        ))}
                        <MultipleChoiceDropdown
                            size="sm"
                            values={[]}
                            choices={notUsedTags}
                            addNewText="(new tag)"
                            placeholder="New tag..."
                            isLoadingChoices={!hasValue(tagsFetch)}
                            onAdd={handleAddTag}
                            onClick={handleToggleTag}
                            onSearch={setTagSearchKeyword}
                        />
                    </Panel>
                </Popper>
            </ClickAwayListener>
        </>
    )
}
